/**
 * Collection of functions for working with clipped
 * and composited items in the orders dialog.
 */

import {reducePrecision} from '@apps-common/geocorn-sdk/geom/ops';
import * as polygonClipping from 'polyclip-ts';

import {getExtent} from '@explorer/util/geom';
import {getUniqueId} from '@explorer/util/items';

export const ITEMS_GROUP_BY = {
  // A value is required otherwise things get weird
  NONE: '-',
  ALL: 'all',
  // This can be any item property.
  STRIP_ID: 'strip_id',
};

const groupItems = (items, groupBy, selectedItems = []) => {
  if (groupBy !== ITEMS_GROUP_BY.NONE) {
    const groups = {};
    if (groupBy === ITEMS_GROUP_BY.ALL) {
      groups['*'] = items;
    } else {
      items.forEach(item => {
        const key = item.properties[groupBy];
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
      });
    }

    return Object.keys(groups).map(key => {
      const groupIds = [];
      const selectedInGroup = [];

      groups[key].forEach(item => {
        const id = getUniqueId(item);
        groupIds.push(id);

        if (selectedItems.includes(id)) {
          selectedInGroup.push(item);
        }
      });

      // This filter function will handle the case in which
      //  a group is only partially selected, example:
      //  a user turns off an underlying scene but groups by strip.
      // When *nothing* in the group is selected, this will return all items
      //  in the group.
      const footprints =
        selectedInGroup.length === 0 ? groups[key] : selectedInGroup;

      // get the unioned geometry
      return {
        type: 'Feature',
        id: groupIds.join(','),
        acquired: groups[key][0]?.properties?.acquired,
        properties: {
          items: groups[key],
        },
        geometry: {
          type: 'MultiPolygon',
          // the filter below is a bit of a hack for when there is a partial
          //  selection (say one scene of three in a strip)
          coordinates: footprints?.length
            ? polygonClipping.union(
                ...footprints.map(feature => {
                  const truncated = reducePrecision(feature.geometry, 10);
                  return truncated.coordinates;
                })
              )
            : [],
        },
      };
    });
  } else {
    // no group-by, no problem, normalize this to
    //  the same groups structure but with only one item
    return items.map(item => ({
      ...item,
      id: getUniqueId(item),
      acquired: item?.properties?.acquired,
      properties: {
        items: [item],
      },
    }));
  }
};

export const toolsObjectToGroupType = obj => {
  const tool = obj?.find(tool => 'composite' in tool);
  if (tool) {
    if (tool.composite.group_by) {
      return ITEMS_GROUP_BY.STRIP_ID;
    } else {
      return ITEMS_GROUP_BY.ALL;
    }
  } else {
    return ITEMS_GROUP_BY.NONE;
  }
};

const clipItems = (items, clipGeometry) => {
  if (clipGeometry) {
    return items.map(item => {
      return {
        ...item,
        geometry: {
          type: 'MultiPolygon',
          coordinates: polygonClipping.intersection(
            clipGeometry.coordinates,
            item.geometry.coordinates
          ),
        },
      };
    });
  }
  // no clip, no problem.
  return items;
};

const eastToWestSort = (a, b) => {
  const extA = getExtent(a.geometry);
  const extB = getExtent(b.geometry);
  return extA.left - extB.left;
};

const acquiredLeastRecentSort = (a, b) => {
  return (
    new Date(a.acquired ?? a.properties.acquired) -
    new Date(b.acquired ?? b.properties.acquired)
  );
};

const acquiredMostRecentSort = (a, b) => {
  return (
    new Date(b.acquired ?? b.properties.acquired) -
    new Date(a.acquired ?? a.properties.acquired)
  );
};

export const SORT_OPTIONS_MAP = {
  EAST_TO_WEST: 'east-to-west-sort',
  ACQUIRED_LEAST_RECENT: 'acquired-least-recent',
  ACQUIRED_MOST_RECENT: 'acquired-most-recent',
};

export const ITEMS_SORT_FUNCTIONS = {
  [SORT_OPTIONS_MAP.EAST_TO_WEST]: eastToWestSort,
  [SORT_OPTIONS_MAP.ACQUIRED_LEAST_RECENT]: acquiredLeastRecentSort,
  [SORT_OPTIONS_MAP.ACQUIRED_MOST_RECENT]: acquiredMostRecentSort,
};

export const calculateOutputFootprints = (
  items,
  clipGeometry,
  groupBy,
  selectedItems = [],
  sortType = SORT_OPTIONS_MAP.ACQUIRED_MOST_RECENT
) => {
  const sortedItems = items.sort(ITEMS_SORT_FUNCTIONS[sortType]);

  // Make it so that "Most Recent" is actually displayed as expected
  // the array submitted will have the most recent id last.
  if (groupBy !== ITEMS_GROUP_BY.NONE) {
    sortedItems.reverse();
  }

  const groupedItems = groupItems(sortedItems, groupBy, selectedItems).sort(
    ITEMS_SORT_FUNCTIONS[sortType]
  );

  return clipItems(groupedItems, clipGeometry).filter(
    item => item.geometry.coordinates.length > 0
  );
};

export const isGroupSelected = (groupedItems, selectedItems) => {
  return groupedItems.properties.items.some(item =>
    selectedItems.includes(getUniqueId(item))
  );
};
